import React, { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import useDocumentTitle from '../hooks/useDocumentTitle'

/**
 * Strona dla adresów, których nie ma.
 *
 * PO CO: dotąd nieistniejący adres zwracał pustą stronę — nagłówek, stopka
 * i nic pomiędzy. Zmierzone na produkcji: `/nie-ma-takiej-strony` odpowiadało
 * HTTP 200 z pustym `#root`. Dla użytkownika ślepy zaułek, dla Google „soft 404",
 * czyli pusta strona zgłoszona jako sprawna.
 *
 * NOINDEX USTAWIANY Z JAVASCRIPTU, bo status HTTP zostaje 200: nginx serwuje SPA
 * pod każdym adresem i nie wie, które trasy są prawdziwe. Googlebot renderuje JS
 * i ten znacznik widzi — to udokumentowane rozwiązanie dla aplikacji jednostronowych.
 * Docelowo lepszy jest prawdziwy status 404 z serwera, ale wymaga wypisania
 * wszystkich poprawnych tras w konfiguracji nginxa; pomyłka w takiej liście
 * wygasza działające strony, więc to osobna, ostrożna zmiana.
 *
 * Znacznik SPRZĄTAMY przy odmontowaniu — inaczej `noindex` zostawałby na kolejnej,
 * całkiem poprawnej stronie, na którą użytkownik przejdzie stąd klikając link.
 */

const SKROTY = [
  { to: '/panele', label: 'Moduły PV' },
  { to: '/inwertery', label: 'Falowniki' },
  { to: '/magazyny', label: 'Magazyny energii' },
  { to: '/ladowarki', label: 'Ładowarki EV' },
  { to: '/dobor-zestawu-pv', label: 'Asystent doboru' },
  { to: '/baza-wiedzy', label: 'Baza wiedzy' },
]

const NieZnaleziono = () => {
  useDocumentTitle('Nie znaleziono strony')
  const location = useLocation()

  useEffect(() => {
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex'
    document.head.appendChild(meta)
    return () => { document.head.removeChild(meta) }
  }, [])

  return (
    <div className="px-4 py-16 sm:py-24 flex justify-center">
      <div className="w-full max-w-xl text-center">
        <p className="text-sm font-semibold text-blue-600">Błąd 404</p>
        <h1 className="mt-2 text-2xl sm:text-3xl font-bold text-gray-900">
          Nie ma takiej strony
        </h1>
        <p className="mt-3 text-sm text-gray-600 leading-6">
          Adres <span className="font-mono text-gray-500 break-all">{location.pathname}</span> nie
          istnieje albo już go nie ma. Produkty znikają z serwisu, gdy sklep wycofa ofertę —
          jeśli trafiłeś tu ze starego linku, poszukaj modelu jeszcze raz.
        </p>

        <Link
          to="/panele"
          className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600
                     text-white text-sm font-semibold no-underline hover:bg-blue-500
                     active:bg-blue-700 transition-colors shadow-sm"
        >
          <MagnifyingGlassIcon className="w-4 h-4" />
          Szukaj produktu
        </Link>

        {/* Linki, a nie samo „wróć": ślepy zaułek ma prowadzić dalej w serwis,
            a nie wypychać z niego. */}
        <div className="mt-8">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
            Może szukasz
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {SKROTY.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm
                           text-gray-700 no-underline hover:border-blue-300 hover:text-blue-700
                           transition-colors"
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default NieZnaleziono
